import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Pencil, Download, Trash2 } from "lucide-react";
import PageShell from "../dashboard/PageShell";

const readOnlyCell = (label: string, value?: React.ReactNode) => (
  <tr>
    <td className="px-4 py-2 text-sm text-gray-700 w-64 align-top bg-gray-50 border-b border-gray-200">
      {label}
    </td>
    <td className="px-4 py-2 text-sm text-gray-900 border-b border-gray-200">
      {value ?? <span className="text-gray-400">-</span>}
    </td>
  </tr>
);

const CouncilDetail: React.FC = () => {
  const navigate = useNavigate();
  const id = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      return parts[parts.length - 1] || "";
    } catch {
      return "";
    }
  }, []);

  const [activeTab, setActiveTab] = useState(() => {
    // Load active tab from localStorage, default to "council-info"
    return localStorage.getItem("councilDetailActiveTab") || "council-info";
  });

  const data = useMemo(
    () => ({
      councilName: "Hội đồng xét duyệt đề tài số 1 năm 2025",
      decisionNumber: "01",
      decisionDate: "20/07/2025",
      councilType: "Hội đồng xét duyệt thuyết minh đề cương",
      field: "Công nghệ thông tin và tự động hóa",
      attachedFile: "Quyết định thành lập HĐXD đề tài số 1.docx",
      members: [
        {
          name: "GS. TSKH Lê Anh Tuấn",
          unit: "Đại học mở",
          role: "Ủy viên thư ký",
        },
        {
          name: "VS. ThS Thượng tá, VS. ThS Lê Anh Tuấn",
          unit: "Phòng Vật liệu/Viện Độ bền nhiệt đới",
          role: "Ủy viên phản biện 2",
        },
        {
          name: "GS. TS Thượng tá, GS. TS Nguyễn Thị Hạnh",
          unit: "Phòng Vật liệu/Viện Độ bền nhiệt đới",
          role: "Ủy viên phản biện 1",
        },
        {
          name: "ThS ThS Nguyễn Văn An",
          unit: "Viện Bơm và Thiết bị thủy lợi",
          role: "Chủ tịch hội đồng",
        },
      ],
    }),
    []
  );

  const handleTabChange = (tabKey: string) => {
    setActiveTab(tabKey);
    localStorage.setItem("councilDetailActiveTab", tabKey);
  };

  return (
    <PageShell title="Chi tiết hội đồng">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => window.history.back()}
              aria-label="Go back"
              className="p-3 rounded-full text-[#337ab7] bg-blue-50 hover:bg-blue-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-800 truncate">
              Chi tiết hội đồng
              <span className="ml-2 text-sm font-normal text-gray-600 truncate align-middle">
                – {data.councilName}
              </span>
            </h1>
          </div>
          <button
            onClick={() => navigate(`/councils/edit/${id}`)}
            className="bg-[#337ab7] text-white px-4 py-2 rounded text-sm font-medium hover:bg-[#2e6da4] transition-colors flex items-center space-x-2 justify-center min-w-[110px]"
          >
            <Pencil className="h-4 w-4" />
            <span>Sửa</span>
          </button>
        </div>

        <div className="bg-white rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6 overflow-x-auto">
              {[
                { key: "council-info", label: "Thông tin hội đồng" },
                { key: "admin-template", label: "Mẫu file hành chính" },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => handleTabChange(t.key)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    activeTab === t.key
                      ? "border-[#337ab7] text-[#337ab7]"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === "council-info" && (
              <div className="space-y-6">
                {/* Council Information Section */}
                <div className="space-y-6">
                  <table className="w-full border border-gray-200 rounded-md overflow-hidden">
                    <tbody>
                      {readOnlyCell("Tên hội đồng", data.councilName)}
                      {readOnlyCell("Số quyết định", data.decisionNumber)}
                      {readOnlyCell("Ngày quyết định", data.decisionDate)}
                      {readOnlyCell("Loại hội đồng", data.councilType)}
                      {readOnlyCell("Lĩnh vực đánh giá", data.field)}
                      {readOnlyCell(
                        "File đính kèm",
                        <div className="flex items-center gap-2">
                          <span>{data.attachedFile}</span>
                          <button className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                            <Download className="h-4 w-4" />
                            <span className="text-sm">Tải về</span>
                          </button>
                        </div>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Council Members Section */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-800">
                    Danh sách thành viên hội đồng
                  </h3>
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-12">
                              Stt
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                              Thành viên hội đồng
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                              Đơn vị công tác
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                              Vai trò
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-20">
                              Xóa
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {data.members.map((member, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                                {index + 1}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                                {member.name}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                                {member.unit}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                                {member.role}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <button
                                  className="text-red-600 hover:text-red-800 p-1 rounded transition-colors"
                                  title="Xóa thành viên"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-between items-center p-4 border-t border-gray-200">
                      <div className="text-sm font-medium text-gray-700">
                        Hiển thị <span className="font-bold">1</span> đến{" "}
                        <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                          <option>10</option>
                          <option>20</option>
                          <option>50</option>
                          <option>100</option>
                        </select>{" "}
                        trong <span className="font-bold">4</span> kết quả
                      </div>
                      <div className="flex space-x-2">
                        <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                          &lt;
                        </button>
                        <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                          1
                        </button>
                        <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                          2
                        </button>
                        <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                          3
                        </button>
                        <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                          &gt;
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "admin-template" && (
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-16">
                          STT
                        </th>
                        <th className="px-3 py-2 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200">
                          Tên mẫu biểu
                        </th>
                        <th className="px-3 py-2 text-center text-xs font-bold text-black uppercase tracking-wider w-24">
                          Tải về
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                          1
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                          <button className="text-blue-600 hover:text-blue-800 underline">
                            QUYẾT ĐỊNH THÀNH LẬP
                          </button>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors">
                            <Download className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                          2
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                          <button className="text-blue-600 hover:text-blue-800 underline">
                            BIÊN BẢN HỌP HỘI ĐỒNG
                          </button>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors">
                            <Download className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm font-medium text-gray-900 border-r border-gray-200">
                          3
                        </td>
                        <td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200">
                          <button className="text-blue-600 hover:text-blue-800 underline">
                            PHIẾU ĐÁNH GIÁ ĐỀ TÀI
                          </button>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button className="text-blue-600 hover:text-blue-800 p-1 rounded transition-colors">
                            <Download className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default CouncilDetail;
